/**
 * A state timeline: one band per series, coloured by what the series WAS over
 * each stretch of the window (VSNP-1261).
 *
 * Component health was drawn as a line chart of `agw_component_up`, which is a
 * 0/1 series. Three things go wrong when a binary series is plotted as a line:
 *
 *  1. The y-axis is meaningless — nobody reads "0.5 up".
 *  2. `MetricsChart` drops a series whose every point is zero, so a component
 *     that has been DOWN for the whole window vanishes from the chart. The one
 *     reading anybody opens this panel to find is the one it cannot show.
 *  3. A missing sample is joined straight across, so "stopped reporting" looks
 *     identical to "stayed up". For a health panel those are opposite facts.
 *
 * A timeline says what the data actually is: a sequence of states with
 * durations. Runs of the same state are coalesced, so a healthy component is
 * one green bar rather than two hundred divs, and a gap is its own colour
 * rather than an interpolation.
 *
 * Deliberately hand-rolled rather than a charting library's widget: the whole
 * component is a flex row of `div`s whose grow factors are durations, it adds
 * no dependency, and it stays readable at the 24 px band height a detail page
 * has room for.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import accessObservabilityService, { type StateSeries } from '../../services/accessObservability.service';
import { resolveTimeRange, type TimeRangeValue } from '../../utils/timeRange';
import {
  STATE_CLASS,
  STATE_LABEL,
  currentState,
  fmtClock,
  toSegments,
  type BandState,
} from '../../utils/stateTimeline';

interface StateTimelineProps {
  title: string;
  /** A PromQL query returning a 0/1 series per band. */
  query: string;
  range: TimeRangeValue;
  /** Labels that name a band, e.g. ['component']. */
  labelKeys?: string[];
  emptyMessage?: string;
  testId?: string;
  /**
   * A tag for a band, shown beside its name and exposed as `data-tag` — e.g.
   * a component the metrics report but the heartbeat does not (VSNP-1438).
   */
  bandTag?: (name: string) => BandTag | null;
  /** A line under the bands explaining what the tags mean. */
  footnote?: ReactNode;
}

export interface BandTag {
  /** Stable machine key, rendered as the row's `data-tag`. */
  key: string;
  label: string;
  title: string;
}

export const StateTimeline = ({
  title,
  query,
  range,
  labelKeys,
  emptyMessage = 'No data',
  testId,
  bandTag,
  footnote,
}: StateTimelineProps) => {
  const [series, setSeries] = useState<StateSeries[]>([]);
  const [window, setWindow] = useState({ start: 0, end: 0, step: 60 });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // The string form, so an inline array prop cannot restart the effect on
  // every render — same reason as QueryPanels.
  const keys = labelKeys?.join(',') ?? '';

  const load = useCallback(async () => {
    const { start, end, step } = resolveTimeRange(range);
    setLoading(true);
    try {
      const wanted = keys ? keys.split(',') : undefined;
      const out = await accessObservabilityService.stateSeries(query, start, end, step, (m) => {
        if (wanted?.length) {
          const parts = wanted.map((k) => m[k]).filter(Boolean);
          if (parts.length) return parts.join(' · ');
        }
        return m.__name__ ?? 'value';
      });
      out.sort((a, b) => a.name.localeCompare(b.name));
      setSeries(out);
      setWindow({ start, end, step });
      setFailed(false);
    } catch {
      // "The query failed" and "nothing reported" are different, and the panel
      // says which (VSNP-1265's rule, applied here too).
      setSeries([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [query, range, keys]);

  useEffect(() => {
    void load();
  }, [load]);

  const span = Math.max(1, window.end - window.start);

  return (
    <div className="card" data-testid={testId}>
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {(['up', 'down', 'gap'] as BandState[]).map((s) => (
            <span key={s} className="inline-flex items-center gap-1">
              <span className={`inline-block h-2 w-3 rounded-sm ${STATE_CLASS[s]}`} />
              {STATE_LABEL[s]}
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : failed ? (
        <p className="text-sm text-warning-800" data-testid={testId ? `${testId}-error` : undefined}>
          The metrics store did not answer, so component health is not shown. This is not the
          same as every component being down.
        </p>
      ) : series.length === 0 ? (
        <p className="text-sm text-gray-400">{emptyMessage}</p>
      ) : (
        <div className="space-y-1.5">
          {series.map((s) => {
            const segs = toSegments(s.points, window.start, window.end, window.step);
            const state = currentState(segs);
            const tag = bandTag?.(s.name) ?? null;
            return (
              <div
                key={s.name}
                className="flex items-center gap-2"
                data-component={s.name}
                data-state={state}
                data-tag={tag?.key ?? ''}
              >
                <span className="flex w-40 shrink-0 items-center gap-1 truncate" title={s.name}>
                  <span className="truncate font-mono text-xs text-gray-600">{s.name}</span>
                  {tag && (
                    <span
                      className="shrink-0 rounded bg-gray-100 px-1 py-0.5 text-[10px] font-medium text-gray-500"
                      title={tag.title}
                      data-testid="band-tag"
                    >
                      {tag.label}
                    </span>
                  )}
                </span>
                <div className="flex h-4 flex-1 overflow-hidden rounded-sm bg-gray-100">
                  {segs.map((seg) => (
                    <div
                      key={`${seg.from}-${seg.to}`}
                      className={STATE_CLASS[seg.state]}
                      style={{ flexGrow: (seg.to - seg.from) / span, flexBasis: 0 }}
                      title={`${STATE_LABEL[seg.state]} · ${fmtClock(seg.from)}–${fmtClock(seg.to)}`}
                    />
                  ))}
                </div>
                <span
                  className={`w-12 shrink-0 text-right text-xs ${
                    state === 'up' ? 'text-success-600' : state === 'down' ? 'text-danger-600' : 'text-gray-400'
                  }`}
                >
                  {STATE_LABEL[state]}
                </span>
              </div>
            );
          })}
          {footnote && (
            <p className="pt-1 text-xs text-gray-500" data-testid={testId ? `${testId}-note` : undefined}>
              {footnote}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
